import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  listStorageProfiles,
  createStorageProfile,
  updateStorageProfile,
  deactivateStorageProfile,
  STORAGE_PROVIDERS,
  STORAGE_PROVIDER_LABELS,
  STORAGE_ACLS,
  type StorageProfile,
  type CreateStorageInput,
  type UpdateStorageInput,
} from '../../api/storageConfigApi.js';

const CODE_RE = /^[A-Z0-9_]{2,40}$/;

interface FormState {
  profileCode: string;
  provider: string;
  region: string;
  bucketOrContainer: string;
  basePrefix: string;
  defaultAcl: string;
  credentials: string;
}

const EMPTY: FormState = {
  profileCode: '',
  provider: 'S3',
  region: '',
  bucketOrContainer: '',
  basePrefix: '',
  defaultAcl: 'PRIVATE',
  credentials: '',
};

function toBase64(text: string): string {
  // btoa handles latin1; encode UTF-8 first for safety.
  return btoa(unescape(encodeURIComponent(text)));
}

export function StorageConfigPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly StorageProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVersion, setEditVersion] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = () =>
    listStorageProfiles(token)
      .then((r) => setRows(r))
      .catch((e) => setError((e as Error).message));

  useEffect(() => {
    let alive = true;
    listStorageProfiles(token)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const set = (k: keyof FormState, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setStatus(null);
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setEditVersion(0);
    setErrors({});
  };

  const startEdit = (p: StorageProfile) => {
    setEditingId(p.id);
    setEditVersion(p.version);
    setErrors({});
    setStatus(null);
    setForm({
      profileCode: p.profileCode,
      provider: p.provider,
      region: p.region ?? '',
      bucketOrContainer: p.bucketOrContainer,
      basePrefix: p.basePrefix ?? '',
      defaultAcl: p.defaultAcl,
      credentials: '',
    });
  };

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!editingId && !CODE_RE.test(form.profileCode))
      e.profileCode = t('storage.config.err.code');
    if (!form.bucketOrContainer.trim())
      e.bucketOrContainer = t('storage.config.err.bucket');
    if (!editingId && !form.credentials.trim())
      e.credentials = t('storage.config.err.credentials');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setStatus(null);
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingId) {
        const body: UpdateStorageInput = {
          bucketOrContainer: form.bucketOrContainer,
          basePrefix: form.basePrefix,
          defaultAcl: form.defaultAcl as StorageProfile['defaultAcl'],
          version: editVersion,
        };
        if (form.credentials.trim())
          (body as { credentialsBase64?: string }).credentialsBase64 =
            toBase64(form.credentials);
        await updateStorageProfile(editingId, body, token);
        setStatus(t('storage.config.updated'));
      } else {
        const body: CreateStorageInput = {
          profileCode: form.profileCode,
          provider: form.provider as StorageProfile['provider'],
          bucketOrContainer: form.bucketOrContainer,
          credentialsBase64: toBase64(form.credentials),
          ...(form.region ? { region: form.region } : {}),
          ...(form.basePrefix ? { basePrefix: form.basePrefix } : {}),
          defaultAcl: form.defaultAcl as StorageProfile['defaultAcl'],
        };
        await createStorageProfile(body, token);
        setStatus(t('storage.config.created'));
      }
      resetForm();
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async (p: StorageProfile) => {
    setError(null);
    try {
      await deactivateStorageProfile(p.id, p.version, token);
      setStatus(t('storage.config.deactivated', { code: p.profileCode }));
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('storage.config.loading')}</p>;

  return (
    <main aria-label="storage-config">
      <h1>{t('storage.config.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      <table aria-label="storage-profiles-table">
        <thead>
          <tr>
            <th>{t('storage.config.col.code')}</th>
            <th>{t('storage.config.col.provider')}</th>
            <th>{t('storage.config.col.bucket')}</th>
            <th>{t('storage.config.col.region')}</th>
            <th>{t('storage.config.col.acl')}</th>
            <th>{t('storage.config.col.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} data-testid={`row-${p.id}`}>
              <td>{p.profileCode}</td>
              <td>{STORAGE_PROVIDER_LABELS[p.provider] ?? p.provider}</td>
              <td>{p.bucketOrContainer}</td>
              <td>{p.region ?? '—'}</td>
              <td>{p.defaultAcl}</td>
              <td>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  data-testid={`edit-${p.id}`}
                >
                  {t('storage.config.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => onDeactivate(p)}
                  data-testid={`deactivate-${p.id}`}
                >
                  {t('storage.config.deactivate')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <form onSubmit={onSubmit} aria-label="storage-form" noValidate>
        <h2>{editingId ? t('storage.config.editTitle') : t('storage.config.newTitle')}</h2>

        <div className="storage-field">
          <label htmlFor="sf-code">{t('storage.config.profileCode')}</label>
          <input
            id="sf-code"
            value={form.profileCode}
            onChange={(e) => set('profileCode', e.target.value)}
            disabled={!!editingId}
            data-testid="field-profileCode"
          />
          {errors.profileCode ? (
            <span data-testid="err-profileCode">{errors.profileCode}</span>
          ) : null}
        </div>

        <div className="storage-field">
          <label htmlFor="sf-provider">{t('storage.config.provider')}</label>
          <select
            id="sf-provider"
            value={form.provider}
            onChange={(e) => set('provider', e.target.value)}
            disabled={!!editingId}
            data-testid="field-provider"
          >
            {STORAGE_PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {STORAGE_PROVIDER_LABELS[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="storage-field">
          <label htmlFor="sf-region">{t('storage.config.region')}</label>
          <input
            id="sf-region"
            value={form.region}
            onChange={(e) => set('region', e.target.value)}
            data-testid="field-region"
          />
        </div>

        <div className="storage-field">
          <label htmlFor="sf-bucket">{t('storage.config.bucket')}</label>
          <input
            id="sf-bucket"
            value={form.bucketOrContainer}
            onChange={(e) => set('bucketOrContainer', e.target.value)}
            data-testid="field-bucketOrContainer"
          />
          {errors.bucketOrContainer ? (
            <span data-testid="err-bucketOrContainer">
              {errors.bucketOrContainer}
            </span>
          ) : null}
        </div>

        <div className="storage-field">
          <label htmlFor="sf-prefix">{t('storage.config.basePrefix')}</label>
          <input
            id="sf-prefix"
            value={form.basePrefix}
            onChange={(e) => set('basePrefix', e.target.value)}
            data-testid="field-basePrefix"
          />
        </div>

        <div className="storage-field">
          <label htmlFor="sf-acl">{t('storage.config.defaultAcl')}</label>
          <select
            id="sf-acl"
            value={form.defaultAcl}
            onChange={(e) => set('defaultAcl', e.target.value)}
            data-testid="field-defaultAcl"
          >
            {STORAGE_ACLS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        <div className="storage-field">
          <label htmlFor="sf-cred">
            {t('storage.config.credentials')} {editingId ? t('storage.config.credKeep') : '*'}
          </label>
          <textarea
            id="sf-cred"
            value={form.credentials}
            onChange={(e) => set('credentials', e.target.value)}
            data-testid="field-credentials"
          />
          {errors.credentials ? (
            <span data-testid="err-credentials">{errors.credentials}</span>
          ) : null}
        </div>

        <button type="submit" disabled={saving} data-testid="submit-storage">
          {saving ? t('storage.config.saving') : editingId ? t('storage.config.updateProfile') : t('storage.config.createProfile')}
        </button>
        {editingId ? (
          <button type="button" onClick={resetForm} data-testid="cancel-edit">
            {t('storage.config.cancel')}
          </button>
        ) : null}
      </form>
    </main>
  );
}
